export interface MobileProduct {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  images: string[];
  features: string[];
  specs?: Record<string, string>;
  highlights: { title: string; value: string; icon: string }[];
  pdfUrl?: string;
  // Structured sections for Tabs
  technicalData: Record<string, string>;
  mainCharacteristics: Record<string, string>;
  connectivity: Record<string, string>;
  physicalCharacteristics: Record<string, string>;
}

export const mobileProducts: MobileProduct[] = [
  {
    id: "rxis203",
    name: "RxIS203",
    subtitle: "Dual SIM IS-Mobile",
    description: `The RxIS203 is a robust intrinsically safe mobile device from Realix, designed for hazardous environments. It features a high-definition FHD display and a powerful 12MP camera for clear documentation in the field.
  
Designed for versatility, it supports Dual SIM Dual Standby functionality, allowing users to manage connectivity efficiently, or switch to a One SIM + One TF Card configuration for expanded storage. The device includes a specialized magnetic charging pin for safe and easy power connection and a loudspeaker designed for clear audio. It is built to withstand exposure to water and hazardous conditions.`,
    images: ["/images/products/mobile/rxis203-front.png"],
    features: [
      "INTRINSICALLY SAFE: Ex Certified for Hazardous Areas",
      "DISPLAY: FHD High Definition Screen",
      "CAMERA: 12MP Camera",
      "CHARGING: Magnetic Charging Pin",
      "SIM: Dual SIM Dual Standby / TF Card Support",
      "AUDIO: Amplified Loudspeaker",
    ],
    highlights: [
      { title: "Camera", value: "12MP", icon: "ion:camera" },
      { title: "Display", value: "FHD", icon: "ion:phone-portrait" },
    ],
    pdfUrl: "/documents/mobile/technical-specification/rxis203.pdf",
    technicalData: {
      Brand: "Realix",
      Model: "RxIS203",
      Camera: "12MP",
      "Charging Interface": "Magnetic charging pin",
    },
    mainCharacteristics: {
      "Display Resolution": "FHD (Full High Definition)",
      "Card Slot": "Dual SIM Card or One SIM + One TF Card",
      Durability: "Ex (Explosion Proof) Rated",
      Audio: "Loudspeaker",
    },
    connectivity: {
      "SIM Configuration": "Dual SIM Dual Standby",
      Expansion: "TF Card Slot",
      Charging: "Magnetic Pin",
    },
    physicalCharacteristics: {
      "Water Resistance": "Yes (See manual for exposure limits)",
      Housing: "Ruggedized for Ex environments",
    },
  },
  {
    id: "rxis202",
    name: "RxIS202",
    subtitle: "IS-Mobile",
    description: `The all new RxIS202 is the product of intense research and our relentless effort to make the rugged phones more user friendly while keeping the safety of the user as prime goal. It's the lightest IS smart-phone with 5G capability in market globally. It’s zone 1 circuitry offers safety in hazardous environments while providing best in class user experience.
  
The assembly houses high performance 2.0GHz Octa core processor with 4400 mAh battery providing a superlative experience with long lasting usability. The 5.7” display with high brightness provides bright and illuminated information even in the brightest and harshest of conditions. The speaker comes with 2W Smart PA amplifier with dual MIC noise reduction and is capable of producing sound at 120dB which comes handy in noisy industrial environment. It is designed and tested to meet global IS standards, ensuring safe operations in hazardous environments.`,
    images: [
      "/images/products/mobile/rxis202-front.png",
      "/images/products/mobile/rxis202-back.png",
    ],
    features: [
      "INTRINSICALLY SAFE: Explosion Proof / ATEX Zone 1/21",
      "LIGHT WEIGHT: One of the lightest with 298 gms",
      "OPERATING TEMPERATURE: RANGES -20°C to 60°C",
      "OPERATING SYSTEM: Android 11.0",
      "ULTRA-RUGGED: Dust and Water-resistant (IP68)",
      "MEMORY: 128GB ROM & 8GB RAM (Micro-SD - Expandable up to 128GB)",
    ],
    highlights: [
      { title: "Network", value: "5G", icon: "ion:wifi" },
      { title: "Battery", value: "4400 mAh", icon: "ion:battery-charging" },
    ],
    pdfUrl: "/documents/mobile/technical-specification/rxis202.pdf",
    technicalData: {
      "Operating System": "Android 11.0",
      Processor: "MT6833, Dimensity 700 2.0GHz (Octa-Core)",
      Memory: "8GB RAM + 128GB ROM",
      "External Memory": "Micro-SD up to 128GB",
      Sensors: "Light, G-Sensor, Proximity, E-Compass, Gyro, Temp, Altitude",
    },
    mainCharacteristics: {
      Display: '5.7" FHD+ IPS (1080 x 2160)',
      Battery: "4400mAh Lithium Ion",
      "Front Camera": "32MP",
      "Rear Camera": "48MP (Main) / 8MP (Wide)",
      "IP Rating": "IP68",
      "ATEX Approval": "II 2G Ex ib IIC T4 Gb | II 2D Ex ib IIIC T120°C Db",
    },
    connectivity: {
      "Network Band": "5G LTE Network",
      SIM: "Dual Nano SIM / Single Nano + TF",
      WiFi: "2.4G & 5.8G / Hotspot 2.0",
      Bluetooth: "5.1",
      Navigation: "GPS + Beidou + Glonass + Galileo",
      NFC: "Yes",
    },
    physicalCharacteristics: {
      Dimensions: "167.5 x 79.8 x 15.6 mm",
      Weight: "298g",
      "Operating Temp": "-20°C to +60°C",
    },
  },
  {
    id: "rxis201",
    name: "RxIS201",
    subtitle: "IS-Smart Phone",
    description: `The RxIS201 is a IS-smart phone which is designed for maximum safety in the field and enhanced usability for first responders. It is engineered to operate safely in hazardous environments, keeping people safe and connected while they get their jobs done.

This phone is especially designed to work in a potentially explosive area and the waterproof feature, with very light housing and nice appearance adds to the overall usability of the model. It’s a powerful mobile phone for zone 1/21 with latest Android operating system.
  
High performance hardware (Octa Core Processor) backed by 8000mAh battery provides a blazing fast experience with long lasting usability. The 6” FHD display with light sensor provides bright and illuminated information even in harshest of conditions. The speaker is capable of producing sound more than 110db, which comes handy in noisy industrial environment. It comes loaded with top of the range features. High-performing and robust design delivers clear and mission-critical voice communications. It is designed and tested to meet global IS standards, ensuring safe operation in hazardous environments.`,
    images: [
      "/images/products/mobile/rxis201-front.png",
      "/images/products/mobile/rxis201-back.png",
    ],
    features: [
      "INTRINSICALLY SAFE: Explosion Proof / ATEX Zone 1/21",
      "HEALTH & SAFETY-SOS: SOS Button / (LWP/Man-Down) GPS, AGPS",
      "OPERATING TEMPERATURE: RANGES -20°C to 60°C",
      "OPERATING SYSTEM: Android 12.0",
      "ULTRA-RUGGED: Dust and Waterproof (IP68)",
      "MEMORY: 64GB ROM & 6GB RAM (Micro-SD - Expandable up to 128GB)",
    ],
    highlights: [
      { title: "Display", value: '6.0" FHD', icon: "ion:phone-portrait" },
      { title: "Battery", value: "8000 mAh", icon: "ion:battery-full" },
    ],
    pdfUrl: "/documents/mobile/technical-specification/rxis201.pdf",
    technicalData: {
      "Operating System": "Android 9.0 (Technical Spec Sheet)",
      Processor: "MTK6763 (2.5GHz Octa Core)",
      Memory: "6GB RAM + 64GB ROM",
      "External Memory": "Micro-SD up to 128GB",
    },
    mainCharacteristics: {
      Display: '6.0" Full HD IPS (1080 x 2160)',
      Battery: "8000mAh Lithium Ion (Charging: 4hrs)",
      "Standby Time": "Up to 600hrs",
      "Talk Time": "Up to 16hrs",
      Cameras: "Rear: 13MP | Front: 5MP",
      "IP Rating": "IP68",
      "ATEX Approval": "II 2G Ex ib IIC T4 Gb | II 2D Ex ib IIIC T135°C Db",
    },
    connectivity: {
      "Network Band": "4G LTE Network",
      SIM: "Dual Nano SIM",
      WiFi: "2.4GHz IEEE 802.11 b/g/n",
      Bluetooth: "4.0",
      GPS: "GPS, AGPS",
    },
    physicalCharacteristics: {
      Dimensions: "172.0 x 84.5 x 17.9 mm",
      Weight: "380g",
      "Operating Temp": "-20°C to +60°C",
      Torch: "LED Flash Light",
    },
  },
  {
    id: "rxis101",
    name: "RxIS101",
    subtitle: "Android IS-Mobile",
    description: `The Realix RxIS101 is an advanced device specifically designed for professionals working in hazardous environments. It offers a perfect blend of exceptional safety features and cutting-edge technology. With its 4G connectivity and dual SIM capability, staying connected with your team has never been easier. The RxIS101 operates on the latest Android 12 operating system, providing a seamless and secure user experience. Its compact 2.83-inch TFT screen ensures clear visibility in any lighting conditions.

Thanks to its long-lasting 4200 mAh battery, the RxIS101 keeps you productive throughout your workday. Real-time communication is made effortless with its Push-to-Talk over cellular functionality. Built to endure tough conditions, the RxIS101 holds an IP68 rating, making it dust-proof, waterproof, and resistant to shocks and vibrations.
  
Additionally, it features NFC technology for secure data transfer and contactless transactions. With its powerful 120 dB sound output, you can communicate clearly even in noisy environments. In case of emergencies, the easily accessible SOS button can be used to signal for assistance. The RxIS101 also comes equipped with a 16MP rear camera, allowing you to capture important visuals with precision. Choose the Realix RxIS101 and unlock the ultimate combination of safety, performance, and innovation in an intrinsically safe mobile device.`,
    images: [
      "/images/products/mobile/rxis101-front.png",
      "/images/products/mobile/rxis101-back.png",
    ],
    features: [
      "Advanced Safety Features & Cutting-Edge Technology",
      "4G Connectivity & Dual SIM",
      "Android 12 Operating System",
      "Push-to-Talk Over Cellular (PoC)",
      "IP68 Dust-proof, Waterproof, Shock & Vibration Resistant",
      "NFC Technology & 120dB Sound Output",
      "SOS Button & 16MP Rear Camera",
    ],
    highlights: [
      { title: "OS", value: "Android 12", icon: "ion:logo-android" },
      { title: "Type", value: "Keypad", icon: "ion:keypad" },
    ],
    pdfUrl: "/documents/mobile/technical-specification/rxis101.pdf",
    technicalData: {
      "Operating System": "Android 12",
      Processor: "MTK6762 (2.0GHz Octa Core)",
      Memory: "4GB RAM + 64GB ROM",
      "External Memory": "Micro-SD up to 2TB",
    },
    mainCharacteristics: {
      Display: '2.83" TFT Rug Display (480 x 640)',
      Battery: "4200mAh Lithium Ion",
      Cameras: "Rear: 16MP | Front: 8MP",
      Buttons: "Full Keypad, SOS, PTT, Customizable Key",
      "IP Rating": "IP68",
      "ATEX Approval": "II 2G Ex ib IIC T4 Gb | II 2D Ex ib IIIC T135°C Db",
    },
    connectivity: {
      Network: "2G/3G/4G LTE Dual SIM",
      WiFi: "2.4GHz & 5GHz",
      Bluetooth: "4.2",
      GPS: "GPS+BDS / GPS+GLONASS",
      NFC: "Yes",
      PoC: "Push-to-Talk Over Cellular",
    },
    physicalCharacteristics: {
      Dimensions: "137.3 x 59.3 x 23.3 mm",
      Weight: "246g",
      "Operating Temp": "-20°C to +60°C",
    },
  },
];
